import { Sparkles, X, ChevronRight, ChevronDown } from 'lucide-react';
import { CoachingResource } from '../services/resource-matching';
import { ResourceCard } from './ResourceCard';
import { useState, useEffect } from 'react';

interface ResourcesSidebarProps {
  resources: CoachingResource[];
  loading: boolean;
  isVisible?: boolean;
  onClose?: () => void;
  topicName?: string;
}

export function ResourcesSidebar({
  resources,
  loading,
  isVisible = true,
  onClose,
  topicName
}: ResourcesSidebarProps) {
  const STORAGE_KEY = 'vzmo_resources_collapsed';
  const [isMinimized, setIsMinimized] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isMinimized));
  }, [isMinimized]);

  if (!isVisible) return null;

  return (
    <>
      <div className="hidden lg:block lg:w-96 flex-shrink-0">
        <div className="sticky top-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-h-[calc(100vh-8rem)]">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gold-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-gold-500" />
                <h3 className="font-semibold text-ink-950">Helpful Resources</h3>
                {resources.length > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gold-500 text-ink-950 rounded-full">
                    {resources.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
                aria-label={isMinimized ? 'Expand resources' : 'Collapse resources'}
                title={isMinimized ? 'Expand resources' : 'Collapse resources'}
              >
                {isMinimized ? (
                  <ChevronRight className="h-5 w-5 transition-transform" />
                ) : (
                  <ChevronDown className="h-5 w-5 transition-transform" />
                )}
              </button>
            </div>
          </div>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isMinimized ? 'max-h-0' : 'max-h-[calc(100vh-12rem)]'
          }`}>
            <div className="overflow-y-auto h-full">
              {loading ? (
                <div className="p-6 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Sparkles className="h-4 w-4 text-gold-500 animate-pulse" />
                    <span>Finding relevant resources for you...</span>
                  </div>
                </div>
              ) : resources.length > 0 ? (
                <div className="p-4 space-y-3">
                  <p className="text-xs text-gray-500 mb-3">
                    {topicName ? (
                      <>
                        Resources for <span className="font-semibold text-gold-600">{topicName}</span>
                      </>
                    ) : (
                      'AI-curated resources based on your conversation'
                    )}
                  </p>
                  {resources.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} showActions={true} />
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    {topicName ? (
                      <>Resources will appear as you discuss {topicName.toLowerCase()}</>
                    ) : (
                      'Resources will appear here as you chat with your coach'
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-300"
        style={{ transform: isVisible && resources.length > 0 ? 'translateY(0)' : 'translateY(100%)' }}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold-500" />
              <h3 className="font-semibold text-ink-950">Helpful Resources</h3>
              <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gold-500 text-ink-950 rounded-full">
                {resources.length}
              </span>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Sparkles className="h-4 w-4 text-gold-500 animate-pulse" />
                  <span>Finding resources...</span>
                </div>
              </div>
            ) : (
              resources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} showActions={true} />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
